import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import capitalize from "lodash/capitalize";

import CartCheckout from "@components/store/cart/cart-checkout";
import CartListingForLocker from "@sharedComponents/cart/cart-listing-locker";
import SendToMyselfCartListing from "@sharedComponents/cart/send-to-many/send-to-myself-cart-listing";
import AccordionAllCollapsable from "@stadium/ui/src/components/accordion/accordion-all-collapsable";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import {
  ANALYTICS_EVENT_ACTION_EXPANDED,
  ANALYTICS_EVENT_ACTION_COLLAPSED,
} from "@stadium/services/analytics/constants/default";
import { ITEMS_TARGET } from "@constants/analytics/checkout";

import useFetchVariantList from "@hooks/cart/use-fetch-variant-list";

import { getCartItemsCount } from "@selectors/cart";
import { getIsLockerShop, getIsPricelessShop } from "@selectors/store";
import { useCheckoutContext } from "@contexts/checkout-context";
import { pluralizeWord } from "@stadium/helpers/string";
import Multimedia from "@sharedComponents/multimedia";
import shipStadium from "@images/svg/heart.svg";

import main from "./main.module.scss";

const CheckoutSummary = ({ children }) => {
  const isLockerShop = useSelector(getIsLockerShop);
  const isPricelessShop = useSelector(getIsPricelessShop);

  const [isSubmittedAddressOpen, setIsSubmittedAddressOpen] = useState(false);
  const [isCartListingOpen, setIsCartListingOpen] = useState(false);

  const itemsCount = useSelector(getCartItemsCount);

  const withAnalytics = useWithAnalytics();

  const toggleCartListingAccordion = () => {
    const isExpanding = !isCartListingOpen;
    withAnalytics(setIsCartListingOpen, {
      track: true,
      target: ITEMS_TARGET,
      action: isExpanding ? ANALYTICS_EVENT_ACTION_EXPANDED : ANALYTICS_EVENT_ACTION_COLLAPSED,
    })(isExpanding);
  };
  const toggleSubmittedAddressAccordion = () => setIsSubmittedAddressOpen((prev) => !prev);

  useEffect(() => {
    if (isPricelessShop) setIsCartListingOpen(true);
  }, [isPricelessShop]);

  const { submittedAddress } = useCheckoutContext();
  const cartProductsData = useFetchVariantList();

  const {
    firstname,
    lastname,
    phone,
    address1,
    address2,
    city,
    stateAbbreviation,
    stateNameNonUs,
    countryName,
    zipcode,
    zipcodeNonUs,
  } = submittedAddress || {};

  const renderSubmittedAddress = () => (
    <article className={main.shippingToSection}>
      <div className={main.addresBlock}>
        <div className={main.userName}>
          {firstname} {lastname}
        </div>
        <div className={main.userPhone}>
          <i />
          {phone}
        </div>
        <div className={main.addressInfo}>
          {[
            address1,
            address2,
            [
              city,
              [stateAbbreviation || stateNameNonUs, zipcode || zipcodeNonUs]
                .filter((s) => s)
                .join(" "),
            ]
              .filter((s) => s)
              .join(", "),
            countryName,
          ]
            .filter((s) => s)
            .join("\r\n")}
        </div>
      </div>
    </article>
  );

  const renderSubmittedAddressAccordion = () => {
    if (!submittedAddress.externalId) return null;
    return (
      <AccordionAllCollapsable
        id="shipping-info"
        isOpen={isSubmittedAddressOpen}
        handleAccordianToggle={toggleSubmittedAddressAccordion}
        accordionCustomClass={main.accordion}
        titleContent={
          <div className={main.accordionHeader}>
            Shipping Information
            <div className={main.collapseIconBlock}>
              <span
                className={` ${main.arrowIcon} ${
                  isSubmittedAddressOpen ? main.iconUp : main.iconDown
                }`}
              />
            </div>
          </div>
        }
        bodyContent={<div className={main.collapseBlock}>{renderSubmittedAddress()}</div>}
      />
    );
  };

  const renderCartListingComponent = () => {
    return (
      <SendToMyselfCartListing
        editable={false}
        fetchingOrder={false}
        className={`your-items ${main.yourItems}`}
        cartProductsData={cartProductsData}
      />
    );
  };

  const renderCartListingAccordion = () => {
    if (isLockerShop) return <CartListingForLocker editable={false} className={main.yourItems} />;

    return (
      <AccordionAllCollapsable
        id="cart-listing"
        isOpen={isCartListingOpen}
        handleAccordianToggle={toggleCartListingAccordion}
        accordionCustomClass={main.itemAccordion}
        headerCustomClass={main.toggleBtn}
        titleContent={
          <>
            <div className={main.headingText}>
              Your{" "}
              {capitalize(
                pluralizeWord("item", {
                  count: itemsCount,
                }),
              )}{" "}
              {itemsCount > 1 ? `(${itemsCount})` : ""}
            </div>
            <i className={main.toggleIcon} />
          </>
        }
        bodyContent={
          <div className={main.collapseBlock}>
            <div className={main.shipFromStadium}>
              <Multimedia
                type="image"
                src={shipStadium}
                width={18}
                height={16}
                alt="SHIPS FROM STADIUM"
                className={""}
              />{" "}
              SHIPS FROM STADIUM:
            </div>
            {renderCartListingComponent()}
          </div>
        }
      />
    );
  };

  return (
    <CartCheckout cartCheckoutClass={main.checkoutOrderSummaryBox}>
      {children}

      {renderSubmittedAddressAccordion()}
      <article className={main.yourItemsContainer}>{renderCartListingAccordion()}</article>
    </CartCheckout>
  );
};

export default CheckoutSummary;
